import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useUserData } from './UserDataContext';
import { SearchService } from '../services/api';
import { GovernanceService } from '../services/GovernanceService';
import { calculateAge } from './utils/timeFormat';
import { toast } from 'sonner';
import { AlertTriangle, Clock, X, Search, Loader2 } from 'lucide-react';

interface PassOwnershipModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    entityType: 'forum' | 'tribe';
    entityId: string;
    lastTransferAt?: any; // Timestamp
}

export function PassOwnershipModal({ open, onOpenChange, entityType, entityId, lastTransferAt }: PassOwnershipModalProps) {
    const { passOwnership, userProfile } = useUserData();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searching, setSearching] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any | null>(null);
    const [confirmText, setConfirmText] = useState('');
    const [step, setStep] = useState<1 | 2>(1);
    const [loading, setLoading] = useState(false);
    // Ownership can only be passed to an existing member, so we load the member
    // roster up-front and filter the user search against it.
    const [memberIds, setMemberIds] = useState<Set<string> | null>(null);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Load the group's member IDs whenever the modal opens.
    useEffect(() => {
        if (!open || !entityId) return;
        let cancelled = false;
        (async () => {
            try {
                const members = await GovernanceService.getGroupMembers(entityType, entityId);
                if (cancelled) return;
                const ids = new Set<string>(
                    (members || []).map((m: any) => String(m.userId || m.id)).filter(Boolean)
                );
                setMemberIds(ids);
            } catch (err) {
                console.error('[PassOwnership] Failed to load members:', err);
                if (!cancelled) setMemberIds(new Set());
            }
        })();
        return () => { cancelled = true; };
    }, [open, entityType, entityId]);

    const resetState = () => {
        setSearchQuery('');
        setSearchResults([]);
        setShowDropdown(false);
        setSelectedUser(null);
        setConfirmText('');
        setStep(1);
    };

    // Mirrors the debounced username search used in DiscoverScreenV2.
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const q = e.target.value;
        setSearchQuery(q);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (q.trim().length >= 1) {
            setSearching(true);
            searchTimeoutRef.current = setTimeout(async () => {
                try {
                    // Firestore rules require the query's ageGroup to match the viewer's
                    // actual ageGroup, or the search silently returns []. Same resolution
                    // order as DiscoverScreenV2.
                    const sAgeGroup: 'adult' | 'minor' = (() => {
                        const stored = (userProfile as any)?.ageGroup;
                        if (stored === 'adult' || stored === 'minor') return stored;
                        const age = calculateAge((userProfile as any)?.dateOfBirth);
                        if (age !== null) return age >= 18 ? 'adult' : 'minor';
                        const storedAge = (userProfile as any)?.age;
                        if (typeof storedAge === 'number') return storedAge >= 18 ? 'adult' : 'minor';
                        return 'minor';
                    })();
                    // searchUsers already filters out the passed currentUserId, so the
                    // current owner can never select themselves.
                    const results = await SearchService.searchUsers(q.toLowerCase(), String(userProfile?.id), sAgeGroup);
                    // Ownership can only go to an existing member: drop the current
                    // owner and anyone not in the group's member roster.
                    const memberOnly = (results || []).filter((u: any) =>
                        String(u.id) !== String(userProfile?.id) &&
                        (memberIds ? memberIds.has(String(u.id)) : false)
                    );
                    setSearchResults(memberOnly);
                    setShowDropdown(true);
                } catch (err) {
                    console.error('[PassOwnership] Search error:', err);
                    setSearchResults([]);
                } finally {
                    setSearching(false);
                }
            }, 500);
        } else {
            setSearchResults([]);
            setShowDropdown(false);
            setSearching(false);
        }
    };

    const handleSelectUser = (u: any) => {
        setSelectedUser(u);
        setShowDropdown(false);
        setSearchResults([]);
        setSearchQuery('');
    };

    // Cooldown Check
    const checkCooldown = () => {
        if (!lastTransferAt) return { active: false, remaining: 0 };

        let lastTime = 0;
        if (typeof lastTransferAt === 'number') lastTime = lastTransferAt;
        else if (lastTransferAt?.toMillis) lastTime = lastTransferAt.toMillis();
        else if (lastTransferAt instanceof Date) lastTime = lastTransferAt.getTime();

        const diff = Date.now() - lastTime;
        const cooldown = 24 * 60 * 60 * 1000; // 24 hours

        if (diff < cooldown) {
            const remaining = Math.ceil((cooldown - diff) / (1000 * 60 * 60)); // Hours
            return { active: true, remaining };
        }
        return { active: false, remaining: 0 };
    };

    const { active: cooldownActive, remaining: cooldownHours } = checkCooldown();

    const handleTransfer = async () => {
        if (step === 1) {
            if (!selectedUser) return;
            setStep(2);
            return;
        }

        if (confirmText !== 'PASS') {
            toast.error("Please type PASS to confirm.");
            return;
        }
        if (!selectedUser) return;

        setLoading(true);
        try {
            await passOwnership(entityType, entityId, String(selectedUser.id));
            // Notify any open entity screen so it can demote the former owner to a
            // regular member immediately (otherwise owner-only UI lingers until reload).
            window.dispatchEvent(new CustomEvent('ownershipTransferred', {
                detail: { entityType, entityId, newOwnerId: String(selectedUser.id) },
            }));
            toast.success('Ownership transferred. You are now a regular member.');
            onOpenChange(false);
            resetState();
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || "Failed to pass ownership");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetState(); }}>
            <DialogContent className="sm:max-w-md bg-white dark:bg-[#1a1a1a] border-black/5 dark:border-white/10">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Pass Ownership
                    </DialogTitle>
                    <DialogDescription>
                        Transfer ownership of this {entityType} to another user.
                    </DialogDescription>
                </DialogHeader>

                {cooldownActive ? (
                    <div className="py-6 flex flex-col items-center justify-center text-center">
                        <Clock className="w-12 h-12 text-amber-500 mb-4" />
                        <h4 className="text-lg font-bold text-black dark:text-white">Cooldown Active</h4>
                        <p className="text-sm text-gray-500 mt-2">
                            Ownership cannot be transferred again for another {cooldownHours} hours.
                        </p>
                    </div>
                ) : (
                    <div className="py-4 space-y-4">
                        {step === 1 ? (
                            <div className="space-y-2">
                                <Label>New Owner</Label>

                                {selectedUser ? (
                                    // Confirmation chip — who the owner is about to transfer to.
                                    <div className="flex items-center gap-3 p-2.5 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/5">
                                        <img
                                            src={selectedUser.image || selectedUser.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.name || '')}&background=random`}
                                            alt={selectedUser.name}
                                            className="w-10 h-10 rounded-full object-cover shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-black dark:text-white truncate">{selectedUser.name}</p>
                                            <p className="text-xs text-black/40 dark:text-white/40 truncate">
                                                @{(selectedUser.username || '').replace(/^@/, '')}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedUser(null)}
                                            className="p-1.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 text-black/50 dark:text-white/50 shrink-0"
                                            aria-label="Clear selection"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/30 dark:text-white/30 pointer-events-none" />
                                        <Input
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            placeholder={`Search ${entityType} members...`}
                                            className="pl-9"
                                            autoComplete="off"
                                        />
                                        {searching && (
                                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/30 dark:text-white/30 animate-spin" />
                                        )}

                                        {showDropdown && (
                                            <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-y-auto rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1a1a1a] shadow-xl">
                                                {searchResults.length > 0 ? searchResults.map((u: any) => (
                                                    <div
                                                        key={u.id}
                                                        className="px-3 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 border-b border-black/5 dark:border-white/5 last:border-b-0"
                                                        onClick={() => handleSelectUser(u)}
                                                    >
                                                        <img
                                                            src={u.image || u.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || '')}&background=random`}
                                                            alt={u.name}
                                                            className="w-9 h-9 rounded-full object-cover shrink-0"
                                                        />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-semibold text-black dark:text-white truncate">{u.name}</p>
                                                            <p className="text-xs text-black/40 dark:text-white/40 truncate">
                                                                @{(u.username || '').replace(/^@/, '')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )) : !searching && (
                                                    <div className="px-4 py-5 text-center text-sm text-black/30 dark:text-white/30">
                                                        No matching members. You can only pass ownership to a member of this {entityType}.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <p className="text-xs text-gray-500">
                                    Warning: This action cannot be undone by you. You will lose all owner privileges immediately.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4 bg-red-50 dark:bg-red-900/10 p-4 rounded-lg border border-red-200 dark:border-red-900/30">
                                <p className="font-bold text-red-600 dark:text-red-400 text-sm">
                                    Are you absolutely sure?
                                </p>
                                {selectedUser && (
                                    <div className="flex items-center gap-2.5">
                                        <img
                                            src={selectedUser.image || selectedUser.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.name || '')}&background=random`}
                                            alt={selectedUser.name}
                                            className="w-8 h-8 rounded-full object-cover shrink-0"
                                        />
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-black dark:text-white truncate">{selectedUser.name}</p>
                                            <p className="text-xs text-black/40 dark:text-white/40 truncate">
                                                @{(selectedUser.username || '').replace(/^@/, '')}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label className="text-xs">Type "PASS" to confirm</Label>
                                    <Input
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                        placeholder="PASS"
                                        className="border-red-300 focus:border-red-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    {!cooldownActive && (
                        <Button
                            onClick={handleTransfer}
                            disabled={loading || (step === 1 && !selectedUser) || (step === 2 && confirmText !== 'PASS')}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {loading ? 'Transferring...' : step === 1 ? 'Next' : 'Confirm Transfer'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
